/**
 * paperSessionDrafts — Paper Session Workflow V2 client + pure helpers.
 *
 * Talks to the read-only validator and the app-local DRAFT store:
 *   POST   /api/v1/paper-session/validate-plan
 *   GET    /api/v1/paper-session/drafts
 *   POST   /api/v1/paper-session/drafts
 *   GET    /api/v1/paper-session/drafts/{id}
 *   PATCH  /api/v1/paper-session/drafts/{id}
 *   DELETE /api/v1/paper-session/drafts/{id}
 *
 * NEVER places an order, NEVER calls a broker/execute endpoint. Drafts are plain
 * app records. If the backend is unreachable, a localStorage fallback keeps drafts
 * working (TEMPORARY / per-browser only — see DRAFT_PERSISTENCE_REPORT.md).
 */
import { apiV1Base } from "./apiConfig";

export interface PaperDraft {
  draft_id: string;
  symbol: string;
  created_ts?: string;
  updated_ts?: string;
  account_label?: string | null;
  side?: string | null;
  entry?: number | string | null;
  size?: number | string | null;
  stop_loss?: number | string | null;
  target?: number | string | null;
  exit_rule?: string | null;
  thesis?: string | null;
  time_horizon?: string | null;
  source_row_snapshot?: Record<string, unknown> | null;
  technical_snapshot?: Record<string, unknown> | null;
  integrity_snapshot?: Record<string, unknown> | null;
  catalyst_snapshot?: Record<string, unknown> | null;
  validation_status?: string | null;
  validation_errors?: string[];
  validation_codes?: string[];
  review_status?: string | null;
}

export interface ValidationResult {
  status: string;
  blocking_codes: string[];
  reasons: string[];
  submission_allowed: boolean;
  owner_authorization_required: boolean;
  live_trading_allowed: boolean;
  paper_trading_allowed: boolean;
  validated_plan?: Record<string, unknown>;
  note?: string;
}

export interface AccountRiskProfile {
  account_label: string;
  account_id?: string | null;
  account_source?: string | null;
  max_position_value?: number | null;
  max_risk_dollars?: number | null;
  max_risk_percent?: number | null;
  max_daily_risk_dollars?: number | null;
  allow_short_watch?: boolean;
  allowed_lanes?: string[];
  notes?: string | null;
  source_status?: string | null;
}

export async function getAccountRiskProfiles(): Promise<AccountRiskProfile[]> {
  try {
    const r = await fetch(`${apiV1Base()}/paper-session/account-risk-profiles`, { cache: "no-store" });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const b = await r.json();
    const profs = (b?.data?.profiles ?? b?.profiles) as AccountRiskProfile[] | undefined;
    return Array.isArray(profs) ? profs : [];
  } catch { return []; }
}

const LS_KEY = "adversyn_paper_session_drafts_v1";

function _num(v: unknown): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(String(v).replace(/[^0-9.\-]/g, ""));
  return Number.isFinite(n) ? n : null;
}

// ─── localStorage fallback (temporary, per-browser) ───────────────────────────
function _lsRead(): Record<string, PaperDraft> {
  try {
    const raw = localStorage.getItem(LS_KEY);
    const obj = raw ? JSON.parse(raw) : {};
    return obj && typeof obj === "object" ? obj : {};
  } catch {
    return {};
  }
}
function _lsWrite(map: Record<string, PaperDraft>) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(map)); } catch { /* ignore */ }
}
function _lsId(): string {
  return "psd_local_" + Math.abs(Math.floor((performance.now() % 1) * 1e9) + Math.floor(performance.now())).toString(36) + Math.floor(performance.now()).toString(36);
}
function _nowIso(): string { return new Date().toISOString(); }

// ─── Validator ────────────────────────────────────────────────────────────────
export async function validatePlan(plan: Record<string, unknown>): Promise<ValidationResult> {
  const r = await fetch(`${apiV1Base()}/paper-session/validate-plan`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ plan }),
  });
  const body = await r.json();
  return (body?.data ?? body) as ValidationResult;
}

// ─── Drafts CRUD (backend primary, localStorage fallback) ─────────────────────
export async function listDrafts(status?: string): Promise<PaperDraft[]> {
  try {
    const qs = status ? `?status=${encodeURIComponent(status)}` : "";
    const r = await fetch(`${apiV1Base()}/paper-session/drafts${qs}`, { cache: "no-store" });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const b = await r.json();
    const drafts = (b?.data?.drafts ?? b?.drafts) as PaperDraft[] | undefined;
    if (Array.isArray(drafts)) return drafts;
    throw new Error("bad shape");
  } catch {
    return Object.values(_lsRead())
      .filter((d) => !status || d.validation_status === status)
      .sort((a, b) => String(b.updated_ts).localeCompare(String(a.updated_ts)));
  }
}

export async function createDraft(d: Partial<PaperDraft>): Promise<PaperDraft> {
  try {
    const r = await fetch(`${apiV1Base()}/paper-session/drafts`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(d),
    });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const b = await r.json();
    return (b?.data?.draft ?? b?.draft) as PaperDraft;
  } catch {
    const map = _lsRead();
    const id = _lsId();
    const draft: PaperDraft = { ...d, draft_id: id, symbol: String(d.symbol || "").toUpperCase(), created_ts: _nowIso(), updated_ts: _nowIso(), review_status: d.review_status || "draft" } as PaperDraft;
    map[id] = draft;
    _lsWrite(map);
    return draft;
  }
}

export async function patchDraft(id: string, patch: Partial<PaperDraft>): Promise<PaperDraft | null> {
  try {
    const r = await fetch(`${apiV1Base()}/paper-session/drafts/${encodeURIComponent(id)}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const b = await r.json();
    return (b?.data?.draft ?? b?.draft) as PaperDraft;
  } catch {
    const map = _lsRead();
    if (!map[id]) return null;
    map[id] = { ...map[id], ...patch, updated_ts: _nowIso() };
    _lsWrite(map);
    return map[id];
  }
}

export async function deleteDraft(id: string): Promise<boolean> {
  try {
    const r = await fetch(`${apiV1Base()}/paper-session/drafts/${encodeURIComponent(id)}`, { method: "DELETE" });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return true;
  } catch {
    const map = _lsRead();
    if (!map[id]) return false;
    delete map[id];
    _lsWrite(map);
    return true;
  }
}

// ─── Pure risk math (display only) ────────────────────────────────────────────
export interface RiskInputs { side?: string | null; entry?: unknown; stop?: unknown; size?: unknown; target?: unknown; }
export interface RiskResult {
  entry: number | null; stop: number | null; size: number | null; target: number | null;
  riskPerShare: number | null; dollarRisk: number | null; positionValue: number | null;
  rewardPerShare: number | null; rewardRiskRatio: number | null; maxLoss: number | null;
  stopDirectionValid: boolean | null; stopDirectionWarning: string | null;
}

const LONG = new Set(["long", "buy"]);
const SHORT = new Set(["short", "sell"]);

export function computeRisk(inp: RiskInputs): RiskResult {
  const side = String(inp.side || "").toLowerCase();
  const entry = _num(inp.entry);
  const stop = _num(inp.stop);
  const size = _num(inp.size);
  const target = _num(inp.target);
  const riskPerShare = entry !== null && stop !== null ? Math.abs(entry - stop) : null;
  const dollarRisk = riskPerShare !== null && size !== null ? riskPerShare * size : null;
  const positionValue = entry !== null && size !== null ? entry * size : null;
  const rewardPerShare = entry !== null && target !== null ? Math.abs(target - entry) : null;
  const rewardRiskRatio = rewardPerShare !== null && riskPerShare ? rewardPerShare / riskPerShare : null;

  let stopDirectionValid: boolean | null = null;
  let stopDirectionWarning: string | null = null;
  if (entry !== null && stop !== null && (LONG.has(side) || SHORT.has(side))) {
    if (LONG.has(side)) {
      stopDirectionValid = stop < entry;
      if (!stopDirectionValid) stopDirectionWarning = "Long stop must be BELOW entry.";
    } else {
      stopDirectionValid = stop > entry;
      if (!stopDirectionValid) stopDirectionWarning = "Short stop must be ABOVE entry.";
    }
  }
  return {
    entry, stop, size, target, riskPerShare, dollarRisk, positionValue,
    rewardPerShare, rewardRiskRatio, maxLoss: dollarRisk,
    stopDirectionValid, stopDirectionWarning,
  };
}

// ─── Local readiness preview (mirrors the backend validator) ──────────────────
const BLOCKING_DQ = new Set(["STALE", "FEED_DIVERGENCE", "CALC_MISMATCH", "WIDE_SPREAD", "DEGRADED"]);
const HIGH_RISK_BAND = new Set(["HIGH_RISK", "DO_NOT_TRADE"]);
const HIGH_RISK_CAT = new Set(["HIGH_RISK_REVIEW", "AVOID"]);

export interface ReadinessPreview { blockers: string[]; reviewOnly: boolean; }

export function localReadiness(args: {
  side?: string | null; entry?: unknown; stop?: unknown; size?: unknown;
  exitRule?: string | null; dqFlags?: string[]; integrityBand?: string | null; catalystStatus?: string | null;
}): ReadinessPreview {
  const blockers: string[] = [];
  const risk = computeRisk({ side: args.side, entry: args.entry, stop: args.stop, size: args.size });
  if (risk.stop === null) blockers.push("Stop-loss required.");
  else if (risk.stopDirectionValid === false) blockers.push(risk.stopDirectionWarning || "Invalid stop direction.");
  if (!args.exitRule || !String(args.exitRule).trim()) blockers.push("Exit rule required.");
  if (risk.size === null) blockers.push("Position size required.");

  const dq = (args.dqFlags || []).map((f) => String(f).toUpperCase());
  const dqHard = dq.filter((f) => BLOCKING_DQ.has(f));
  const band = String(args.integrityBand || "").toUpperCase();
  const cat = String(args.catalystStatus || "").toUpperCase();
  const reviewOnly = dqHard.length > 0 || HIGH_RISK_BAND.has(band) || HIGH_RISK_CAT.has(cat);
  if (dqHard.length) blockers.push(`Blocking data-quality flags: ${dqHard.join(", ")}.`);
  if (HIGH_RISK_BAND.has(band)) blockers.push(`Integrity band ${band} — blocked setup.`);
  if (HIGH_RISK_CAT.has(cat)) blockers.push(`Catalyst ${cat} — blocked setup.`);

  return { blockers, reviewOnly };
}
